/**
 * O instante da conclusão de um evento de cobrança, como a tela o monta.
 *
 * A regra combinada, e o porquê de cada parte:
 *
 *   não mexeu     → não manda nada, e o backend carimba AGORA. Melhor do que mandar o relógio do
 *                   navegador: quem conclui "neste momento" quer o momento do registro, e o
 *                   servidor é a única testemunha confiável disso.
 *   mexeu na data → meia-noite LOCAL daquele dia. É o que "concluí no dia 5" quer dizer.
 *   "+ horário"   → a hora informada, também local.
 *
 * A conversão para UTC acontece AQUI, na borda, porque só a tela conhece o fuso de quem olha —
 * `toIsoInstant` faz `new Date("2026-09-08T00:00")`, que o JavaScript lê como hora local, e
 * `toISOString()` devolve o instante. O backend recebe instante pronto e não adivinha fuso.
 */
import { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';

export interface InstantDraft {
    date: string;
    time: string;
    showTime: boolean;
    /** já foi mexido? é isto que distingue "agora" de "meia-noite deste dia" */
    touched: boolean;
}

/** Pré-carregado com o instante atual, no fuso de quem está olhando. */
export const nowDraft = (): InstantDraft => {
    const now = new Date();
    const two = (n: number) => String(n).padStart(2, '0');
    return {
        date: `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())}`,
        time: `${two(now.getHours())}:${two(now.getMinutes())}`,
        showTime: false,
        touched: false,
    };
};

/** Aplica uma mudança do `PaymentDateInput` marcando o rascunho como mexido. */
export const touchDraft = (
    draft: InstantDraft,
    next: { date: string; time: string; showTime: boolean },
): InstantDraft => ({ ...next, touched: true });

/**
 * O instante para o payload — `undefined` quando ninguém mexeu, que é o sinal para o backend
 * carimbar agora.
 */
export const draftInstant = (draft: InstantDraft): string | undefined =>
    draft.touched ? toIsoInstant(draft.date, draft.time, draft.showTime) : undefined;
